import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Tooltip,
  tooltipClasses,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Icons
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Policy as PolicyIcon,
  Assessment as AssessmentIcon,
  Article as ArticleIcon,
  Business as OrganizationIcon,
  Report as ReportingIcon,
  Article as DocumentIcon,
  PlaylistAddCheck as RiskIcon,
  People as StakeholderIcon,
  Security as SecurityIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@mui/icons-material";

// Styled tooltip (matching the legal sidebar)
const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    borderRadius: "1rem",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const CompliceSidebar = () => {
  const theme = useTheme();
  const location = useLocation();

  const [activeMenu, setActiveMenu] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleMenuClick = (index) => {
    if (!collapsed) {
      setActiveMenu(activeMenu === index ? null : index);
    }
  };

  const handleDrawerToggle = () => {
    setCollapsed(!collapsed);
  };

  const drawerWidth = collapsed ? 72 : 280;

  const menuItems = [
    {
      id: 1,
      title: "Dashboard",
      icon: <DashboardIcon />,
      path: "/compliance",
      subItems: [
        { name: "Overview", path: "/compliance", icon: <DashboardIcon /> },
      ],
    },
    {
      id: 2,
      title: "Compliance Management",
      icon: <PolicyIcon />,
      path: "/RegulatoryComplice",
      subItems: [
        {
          name: "Regulatory Tracking",
          path: "/RegulatoryComplice",
          icon: <PolicyIcon />,
        },
        {
          name: "Compliance Audits",
          path: "/CompliceAudit",
          icon: <ReportingIcon />,
        },
        {
          name: "Compliance Policies",
          path: "/ComplicePolicy",
          icon: <SecurityIcon />,
        },
      ],
    },
    {
      id: 3,
      title: "Risk Management",
      icon: <RiskIcon />,
      path: "/CompliceRiskIdentification",
      subItems: [
        {
          name: "Risk Identification",
          path: "/CompliceRiskIdentification",
          icon: <RiskIcon />,
        },
        {
          name: "Risk Analysis",
          path: "/CompliceRiskAnalysis",
          icon: <AssessmentIcon />,
        },
        {
          name: "Mitigation Strategies",
          path: "/CompliceMitigationStrategies",
          icon: <SecurityIcon />,
        },
      ],
    },
    {
      id: 4,
      title: "Document Control",
      icon: <DocumentIcon />,
      path: "/upload",
      subItems: [
        {
          name: "Document Repository",
          path: "/upload",
          icon: <DocumentIcon />,
        },
        {
          name: "Document Approval",
          path: "/CompliceDocumentApproval",
          icon: <ArticleIcon />,
        },
      ],
    },
    {
      id: 5,
      title: "Stakeholder Management",
      icon: <StakeholderIcon />,
      path: "/CompliceStakeholderManagement",
      subItems: [
        {
          name: "Stakeholder Directory",
          path: "/CompliceStakeholderManagement",
          icon: <StakeholderIcon />,
        },
        {
          name: "External Relationships",
          path: "/CompliceRelationshipManagement",
          icon: <OrganizationIcon />,
        },
      ],
    },
    {
      id: 6,
      title: "Reporting",
      icon: <ReportingIcon />,
      path: "/CompliceReport",
      subItems: [
        {
          name: "Compliance Reports",
          path: "/CompliceReport",
          icon: <ReportingIcon />,
        },
        {
          name: "Audit Trails",
          path: "/CompliceAuditTrails",
          icon: <AssessmentIcon />,
        },
      ],
    },
  ];

  useEffect(() => {
    setActiveMenu(1);
  }, []);

  const renderMenuItem = (item) => (
    <Box key={item.id}>
      <ListItemButton
        onClick={() => handleMenuClick(item.id)}
        selected={activeMenu === item.id}
        sx={{
          minHeight: 48,
          px: collapsed ? 1.5 : 2.5,
          margin: "4px 16px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: theme.palette.primary.light,
            transform: "translateX(4px)",
            "& .MuiListItemText-primary": {
              color: theme.palette.primary.main,
              fontWeight: "bold",
            },
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 3,
            justifyContent: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Tooltip
            title={item.title}
            placement="right"
            disableHoverListener={!collapsed}
          >
            {item.icon}
          </Tooltip>
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                },
              }}
            />
            {activeMenu === item.id ? (
              <KeyboardArrowRight sx={{ color: theme.palette.primary.main }} />
            ) : (
              <KeyboardArrowDown sx={{ color: theme.palette.primary.main }} />
            )}
          </>
        )}
      </ListItemButton>

      {!collapsed && (
        <Collapse in={activeMenu === item.id} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subItems.map((subItem, index) => (
              <ListItemButton
                key={index}
                component={Link}
                to={subItem.path}
                sx={{
                  pl: 4,
                  margin: "2px 16px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    transform: "translateX(4px)",
                    "& .MuiListItemText-primary": {
                      color: theme.palette.primary.main,
                      fontWeight: "bold",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: 2,
                    justifyContent: "center",
                    color: theme.palette.primary.main,
                  }}
                >
                  {React.cloneElement(subItem.icon, {
                    style: { fontSize: 16 },
                  })}
                </ListItemIcon>
                <ListItemText
                  primary={subItem.name}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.8rem",
                      color:
                        location.pathname === subItem.path
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      fontWeight:
                        location.pathname === subItem.path ? 600 : 400,
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Link
            to="/Dashboard"
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                component="span"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                }}
              >
                Compliance
              </Typography>
              <Typography
                variant="h6"
                component="span"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  ml: 1,
                }}
              >
                portal
              </Typography>
            </Box>
          </Link>
        )}
        <IconButton onClick={handleDrawerToggle} size="small">
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        {collapsed ? (
          // Collapsed sidebar with tooltips
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 2,
            }}
          >
            {menuItems.map((item) => (
              <LightTooltip
                key={item.id}
                title={
                  <List style={{ listStyle: "none", padding: "0.5rem" }}>
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      sx={{ display: "block", padding: "0.25rem 0" }}
                    >
                      <ListItemText primary={item.title} />
                    </ListItemButton>
                    {item.subItems.map((subItem, i) => (
                      <ListItemButton
                        key={i}
                        component={Link}
                        to={subItem.path}
                        sx={{ display: "block", padding: "0.25rem 0", pl: 2 }}
                      >
                        <ListItemText primary={subItem.name} />
                      </ListItemButton>
                    ))}
                  </List>
                }
                arrow
                placement="right-end"
              >
                <IconButton
                  sx={{
                    color:
                      location.pathname === item.path
                        ? theme.palette.primary.main
                        : theme.palette.text.disabled,
                    mb: 1,
                    "&:hover": {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {item.icon}
                </IconButton>
              </LightTooltip>
            ))}
          </Box>
        ) : (
          <List sx={{ pt: 2 }}>{menuItems.map(renderMenuItem)}</List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <motion.div
      animate={{ width: drawerWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100%",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: drawerWidth,
          height: "100%",
          borderRight: "1px solid",
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          position: "fixed",
          top: 64,
          left: 0,
          bottom: 0,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: !collapsed ? "3px" : "0px",
            backgroundColor: theme.palette.primary.main,
            transition: "width 0.3s ease-in-out",
            zIndex: 1,
          },
        }}
      >
        {drawer}
      </Box>
    </motion.div>
  );
};

export default CompliceSidebar;
